<template>
    <AdminBlock :title="'Invoiceable Jobs for ' + customer.name">

        <!-- Said once, plainly, above the table. The radios enforce it and the
             server rejects anything else, but neither of those explains why the
             screen stopped letting an operator do what it used to. -->
        <p class="text-muted mb-3">
            One invoice covers one job. Pick the job to invoice, then view its draft.
        </p>

        <table class="table table-bordered table-striped table-vcenter dataTable" ref="datatablePickJobsList" style="width: 100%;">
            <thead>
                <tr>
                    <th>Pick for Invoice</th>
                    <th>Job Ref</th>
                    <th>Contract</th>
                    <th>Purchase Order</th>
                    <th>Vehicle</th>
                    <th>Journey</th>
                    <th>Collection Date</th>
                    <th>Delivery Date</th>
                </tr>
            </thead>
            <tbody></tbody>
        </table>
    </AdminBlock>
</template>

<script>

import AdminBlock from "@/components/movements/components/AdminBlock.vue";
import { usePoActions } from "@/composables/usePoActions";

export default {
    components: {
        AdminBlock
    },
    props: {
        ajaxUrl: String,
        jobs: {
            type: Array,
            default: () => [],
            required: false
        },
        customer: {
            type: Object,
            default: () => {},
            required: true
        }
    },

    setup() {
        // The same confirmations the PO Manager uses, from the same place: an
        // operator chasing a purchase order from here must not be told
        // something different from one chasing it from the worklist.
        return usePoActions();
    },

    mounted() {

        const escapeHtml = this.escapeHtml;

        const table = window.jQuery(this.$refs.datatablePickJobsList).on('preXhr.dt', (e, settings, data) => {
            data.filters = this.filters;
        }).DataTable({
            data: this.jobs,
            order: [[0, 'desc']],
            columns: [
                {
                    data: 'id',
                    orderable: false,
                    searchable: false,
                    width: '70px',
                    // A radio rather than a checkbox, all sharing one name, so
                    // the screen cannot express a selection the server will
                    // reject. A blocked job gets no control at all: a disabled
                    // radio still reads as "pick this", and the operator's
                    // actual question is what to do about the missing PO —
                    // which is answered in the Purchase Order column.
                    render: function (data, type, row) {
                        if (row.po && !row.po.selectable) {
                            return '';
                        }

                        return '<input type="radio" name="job_ids[]" value="' + row.id + '">';
                    }
                },
                {
                    data: 'reference',
                },
                {
                    data: 'contract.name'
                },
                {
                    // Blocked rows are listed, not hidden. Hiding one would
                    // leave the operator hunting for a job that is right there
                    // and merely unbillable, and there would be nowhere to put
                    // the button that unblocks it.
                    data: 'client_ref',
                    render: function (data, type, row) {
                        if (type !== 'display' || !row.po || !row.po.blocked) {
                            return data;
                        }

                        let cell = '<span class="badge bg-warning text-dark d-inline-block mb-1">'
                            + escapeHtml(row.po.badge) + '</span>';

                        // Actionable is asked separately from blocked: the
                        // cool-down gates every strike, so a job can have spent
                        // its reminders and still be waiting. Offering the
                        // button anyway would offer what the service refuses.
                        //
                        // D28: only the chase is offered here. Invoicing a job
                        // with no purchase order is irreversible and now has one
                        // home — the Invoicing Manager — and two routes to the
                        // same act would make that screen decorative. A job that
                        // has crossed over is named, with somewhere to go, rather
                        // than left looking merely stuck.
                        const crossedOver = row.po.action
                            && row.po.action.stage === 'force_fill';

                        if (crossedOver) {
                            cell += '<br><a class="text-nowrap small" href="'
                                + escapeHtml(route('movements.invoicing.index')) + '">'
                                + 'Awaiting an invoicing decision</a>';
                        } else if (row.po.actionable && row.po.action) {
                            cell += '<br><button type="button" class="btn btn-sm '
                                + row.po.action.buttonClass + ' po-action-button text-nowrap" '
                                + "data-po-action='" + JSON.stringify(row.po.action) + "'>"
                                + escapeHtml(row.po.action.label) + '</button>';
                        } else if (row.po.available_at) {
                            cell += '<br><span class="text-muted text-nowrap small">Available '
                                + escapeHtml(row.po.available_at) + '</span>';
                        }

                        return cell;
                    }
                },
                {
                    data: 'vehicle.reg'
                },
                {
                    searchable: false,
                    orderable: false,
                    width: '200px',
                    render: function (data, type, row) {
                        return row.collection_locale + '<br> to ' + row.delivery_locale;
                    },
                },
                {
                    data: 'collection_date'
                },
                {
                    data: 'delivery_date'
                },
            ]
        });

        // Delegated on the table body, as on the PO Manager: DataTables rebuilds
        // every row on every draw, so a handler bound to the buttons themselves
        // would survive exactly one page.
        window.jQuery(table.table().body()).on('click', '.po-action-button', (event) => {
            const payload = JSON.parse(event.currentTarget.getAttribute('data-po-action'));

            // A full reload rather than a redraw: this table is rendered from
            // data the server put in the page, so the job's new reminder count
            // and cool-down are not in the browser to redraw from.
            this.confirmThenProcess(payload, () => window.location.reload());
        });

        // watch api to use table without saving it to data
        this.$watch(
            'filters',
            () => table.ajax.reload(null, false),
            {
                deep: true
            }
        );

    }
}
</script>
